import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { SlidersHorizontal, ChevronDown, Loader2, ChevronLeft, ChevronRight, X, Zap, ShieldCheck, Package, Facebook, Instagram, Linkedin, Youtube, Twitter, MessageCircle, BadgePercent } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import ProductCard from "@/components/ProductCard";
import TrustBadges from "@/components/TrustBadges";
import { setSEO } from "@/lib/seo";
import { featuredCompare, brandOf } from "@/lib/catalog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// "All Products" order (shared with the browser cache + backend sort=featured):
// hybrid inverters, on-grid, micro, batteries, ESS, then the rest; cheapest first.

export default function StorePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("category") || "");
  const [sort, setSort] = useState("featured");
  const [stockFilter, setStockFilter] = useState("");
  const [mobileSidebar, setMobileSidebar] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [brandFilter, setBrandFilter] = useState(searchParams.get("brand") || "");
  const [brands, setBrands] = useState([]);
  const categoryScrollRef = useRef(null);
  const apiSettled = useRef(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      image: "/images/gronsol/banner-hybrid.jpg",
      title: "Gronsol - Made in India Inverters",
      subtitle: "Flat 5% OFF on every Gronsol inverter & battery",
      description: "On-grid 1.5-136 kW, hybrid 3-80 kW, micro inverters and LiFePO4 batteries from Surat, Gujarat. Up to 10-year warranty, datasheets for every model.",
      badge: "New Brand - 5% OFF",
      badgeIcon: <BadgePercent className="h-3.5 w-3.5" />,
      gradient: "from-emerald-950/90 via-emerald-900/70 to-transparent",
      accentColor: "bg-red-600",
      cta: "Shop Gronsol",
      href: "/brands/gronsol"
    },
    {
      image: "/images/feston/life-hybrid-home.jpg",
      title: "Feston - Always On",
      subtitle: "Flat 4% OFF on Feston inverters & batteries",
      description: "On-grid 1.5-136 kW, hybrid 3-80 kW, micro inverters and LiFePO4 batteries. BIS, BEE & IEC certified, up to 10-year warranty.",
      badge: "New Brand - 4% OFF",
      badgeIcon: <BadgePercent className="h-3.5 w-3.5" />,
      gradient: "from-blue-950/90 via-blue-900/70 to-transparent",
      accentColor: "bg-red-600",
      cta: "Shop Feston",
      href: "/brands/feston"
    },
    {
      image: "https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1200&q=80",
      title: "Best Price Guarantee",
      subtitle: "Unbeatable rates on every component",
      description: "Single items at lowest prices with additional discounts on bulk purchases. Premium quality solar components at prices that power your profits.",
      badge: "Best Prices",
      badgeIcon: <ShieldCheck className="h-3.5 w-3.5" />,
      gradient: "from-emerald-900/90 via-emerald-900/70 to-transparent",
      accentColor: "bg-emerald-500",
      cta: "Explore Products"
    },
    {
      image: "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=1200&q=80",
      title: "Bulk & Bundle Deals",
      subtitle: "Wholesale pricing for contractors",
      description: "Complete solar kits and bundle packages at wholesale prices. Perfect for contractors, installers, and large-scale projects.",
      badge: "Bulk Savings",
      badgeIcon: <Package className="h-3.5 w-3.5" />,
      gradient: "from-blue-900/90 via-blue-900/70 to-transparent",
      accentColor: "bg-blue-500",
      cta: "View Deals"
    },
    {
      image: "https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=1200&q=80",
      title: "MC4 Connector Special",
      subtitle: "Limited time offer - just ₹10/pair",
      description: "Stock up on MC4 connectors at unbeatable prices. High-quality, weather-resistant connectors for all your solar installations.",
      badge: "Hot Deal",
      badgeIcon: <Zap className="h-3.5 w-3.5" />,
      gradient: "from-orange-900/90 via-orange-900/70 to-transparent",
      accentColor: "bg-orange-500",
      cta: "Shop Now"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const scrollCategories = (direction) => {
    if (categoryScrollRef.current) {
      const scrollAmount = 300;
      categoryScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const fetchProducts = useCallback(async (p = 1, append = false) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const params = { page: p, limit: 20, sort };
      if (selectedCategory) params.category = selectedCategory;
      const searchQuery = searchParams.get("search");
      if (searchQuery) params.search = searchQuery;
      if (stockFilter) params.in_stock = stockFilter;
      if (brandFilter) params.brand = brandFilter;
      const { data } = await axios.get(`${API}/products`, { params });
      apiSettled.current = true;
      setProducts(prev => append ? [...prev, ...data.products] : data.products);
      setTotal(data.total);
      setTotalPages(data.total_pages);
      setPage(p);
      setHasMore(p < data.total_pages);
    } catch (err) {
      console.error("Error fetching products:", err);
    } finally {
      if (append) setLoadingMore(false);
      else setLoading(false);
    }
  }, [selectedCategory, sort, stockFilter, brandFilter, searchParams]);

  const fetchCategories = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/categories`);
      setCategories(data);
    } catch (err) { console.error(err); }
  }, []);

  // Instant first paint from the static build snapshots (/products.json,
  // /categories.json) while the live API - hosted on a free tier that can
  // cold-start for 30s+ - wakes up. Live data replaces it on arrival.
  useEffect(() => {
    if (searchParams.get("search") || searchParams.get("category") || searchParams.get("brand")) return;
    (async () => {
      try {
        const [pr, cr] = await Promise.all([
          fetch("/products.json").then((r) => r.json()),
          fetch("/categories.json").then((r) => r.json()).catch(() => null),
        ]);
        if (Array.isArray(cr) && cr.length) setCategories((prev) => (prev.length ? prev : cr));
        if (apiSettled.current) return;
        const list = (pr.products || pr || [])
          .filter((pd) => pd && !pd.deleted)
          .sort(featuredCompare);
        if (list.length) {
          setProducts((prev) => (prev.length ? prev : list.slice(0, 20)));
          setTotal(list.length);
          setTotalPages(Math.ceil(list.length / 20));
          setHasMore(list.length > 20);
          setLoading(false);
        }
      } catch (e) { /* snapshot unavailable - the API path still loads */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  // Brand filter options (served instantly from the browser cache).
  const loadBrands = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/products`, { params: { limit: 5000 } });
      const counts = {};
      for (const p of data.products || []) {
        const b = brandOf(p);
        if (b) counts[b] = (counts[b] || 0) + 1;
      }
      setBrands(Object.entries(counts).sort((a, b) => a[0].localeCompare(b[0])));
    } catch (e) { /* filter just stays empty */ }
  }, []);
  useEffect(() => { loadBrands(); }, [loadBrands]);
  useEffect(() => {
    window.addEventListener("glannu:data-updated", loadBrands);
    return () => window.removeEventListener("glannu:data-updated", loadBrands);
  }, [loadBrands]);

  // The browser cache found newer prices/products: redraw with them (no reload needed).
  useEffect(() => {
    const onUpdate = () => { fetchCategories(); fetchProducts(1, false); };
    window.addEventListener("glannu:data-updated", onUpdate);
    return () => window.removeEventListener("glannu:data-updated", onUpdate);
  }, [fetchCategories, fetchProducts]);

  useEffect(() => {
    const searchQ = searchParams.get("search");
    if (selectedCategory) {
      setSEO({
        title: `${selectedCategory} - Buy Online at Wholesale Prices`,
        description: `Shop ${selectedCategory.toLowerCase()} online at Glannu Solar Store, Pune. Genuine products, wholesale prices, shipping across India and Maharashtra.`,
        canonical: `https://solar.glannu.com/?category=${encodeURIComponent(selectedCategory)}`,
      });
    } else if (searchQ) {
      setSEO({ title: `Search: ${searchQ}`, canonical: "https://solar.glannu.com/" });
    } else {
      setSEO({ canonical: "https://solar.glannu.com/" });
    }
  }, [selectedCategory, searchParams]);

  useEffect(() => {
    fetchProducts(1, false);
  }, [fetchProducts]);

  // Infinite scroll with Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          fetchProducts(page + 1, true);
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    const sentinel = document.getElementById('infinite-scroll-sentinel');
    if (sentinel) observer.observe(sentinel);

    return () => {
      if (sentinel) observer.unobserve(sentinel);
    };
  }, [hasMore, loadingMore, loading, page, fetchProducts]);

  const handleCategoryClick = (cat) => {
    setSelectedCategory(cat === selectedCategory ? "" : cat);
    setMobileSidebar(false);
  };

  return (
    <main className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Hero Banner Carousel */}
      <div data-testid="store-hero" className="relative rounded-2xl overflow-hidden mb-8 shadow-xl" style={{ height: '320px' }}>
        {/* All slides stacked - crossfade via opacity */}
        {slides.map((slide, index) => (
          <div
            key={index}
            className="absolute inset-0 transition-opacity duration-700 ease-in-out"
            style={{ opacity: currentSlide === index ? 1 : 0, zIndex: currentSlide === index ? 1 : 0 }}
          >
            {/* Background image */}
            <img
              src={slide.image}
              alt={slide.title}
              className="absolute inset-0 w-full h-full object-cover"
              loading={index === 0 ? "eager" : "lazy"}
            />
            {/* Gradient overlay */}
            <div className={`absolute inset-0 bg-gradient-to-r ${slide.gradient}`} />
            {/* Bottom vignette */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />

            {/* Content */}
            <div className="relative z-10 h-full flex flex-col justify-center px-8 sm:px-12 lg:px-16 max-w-2xl">
              <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-white text-xs font-semibold mb-4 w-fit ${slide.accentColor}`}>
                {slide.badgeIcon}
                {slide.badge}
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-2 drop-shadow-lg">
                {slide.title}
              </h1>
              <p className="text-white/80 text-sm sm:text-base font-medium mb-1">{slide.subtitle}</p>
              <p className="text-white/60 text-sm sm:text-base max-w-lg leading-relaxed mb-6 hidden sm:block">
                {slide.description}
              </p>
              <div>
                {slide.href ? (
                  <Link to={slide.href} className="inline-flex items-center gap-2 bg-white text-neutral-900 font-semibold text-sm px-6 py-2.5 rounded-lg hover:bg-neutral-100 transition-colors shadow-lg">
                    {slide.cta}
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                ) : (
                  <button className="inline-flex items-center gap-2 bg-white text-neutral-900 font-semibold text-sm px-6 py-2.5 rounded-lg hover:bg-neutral-100 transition-colors shadow-lg">
                    {slide.cta}
                    <ChevronRight className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 hidden sm:flex items-center justify-center text-white hover:bg-white/40 transition-all"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 hidden sm:flex items-center justify-center text-white hover:bg-white/40 transition-all"
        >
          <ChevronRight className="h-5 w-5" />
        </button>

        {/* Dots + Progress */}
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2.5 z-20">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className="relative h-1.5 rounded-full overflow-hidden transition-all duration-300"
              style={{ width: currentSlide === index ? '2rem' : '0.5rem' }}
            >
              <span className={`absolute inset-0 rounded-full transition-colors ${currentSlide === index ? 'bg-white' : 'bg-white/40 hover:bg-white/60'}`} />
            </button>
          ))}
        </div>
      </div>

      <TrustBadges className="mb-6" />

      <Link to="/brands/gronsol" data-testid="gronsol-strip" className="group mb-6 flex flex-col sm:flex-row items-center gap-4 rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white p-4 hover:shadow-md transition-shadow">
        <img src="/images/gronsol/hybrid-lineup.jpg" alt="Gronsol hybrid inverters" className="h-16 sm:h-20 w-auto object-contain" loading="lazy" />
        <div className="flex-1 text-center sm:text-left">
          <p className="font-heading font-semibold text-neutral-900">New on Glannu: <span className="text-emerald-700">Gronsol</span> - Made in India inverters &amp; LFP batteries</p>
          <p className="text-sm text-neutral-600">On-grid, hybrid &amp; micro inverters from 1.5 kW to 136 kW. Flat 5% off list price, datasheet for every model.</p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 text-white text-sm font-semibold px-4 py-2 group-hover:bg-red-700 whitespace-nowrap">5% OFF - Shop now <ChevronRight className="h-4 w-4" /></span>
      </Link>

      <div className="space-y-6">
        {/* Categories Horizontal Scroll */}
        <div className="bg-white rounded-xl border border-neutral-200 p-4">
          {selectedCategory && (
            <div className="mb-3">
              <Button variant="ghost" size="sm" onClick={() => handleCategoryClick("")} className="text-brand-primary hover:text-brand-primary-hover">
                <X className="h-4 w-4 mr-1" /> Clear
              </Button>
            </div>
          )}
          <div className="relative">
            <Button
              variant="outline"
              size="icon"
              onClick={() => scrollCategories('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md flex-shrink-0"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div ref={categoryScrollRef} className="overflow-x-auto scrollbar-hide px-10">
              <div className="flex gap-3 pb-2">
                <button
                  data-testid="category-filter-all"
                  onClick={() => handleCategoryClick("")}
                  className={`flex flex-col items-center p-3 rounded-lg border-2 transition-all hover:shadow-md flex-shrink-0 ${!selectedCategory ? 'border-brand-primary bg-brand-primary/5' : 'border-neutral-200 hover:border-neutral-300'}`}
                >
                  <img src="https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=100&q=60" alt="All Products" className="w-20 h-20 rounded-lg object-cover mb-2" loading="lazy" />
                  <span className="text-sm font-medium text-center">All Products</span>
                </button>
                {categories.map(cat => (
                  <button
                    key={cat.name}
                    data-testid={`category-filter-${cat.name.toLowerCase().replace(/[\s&]+/g, '-')}`}
                    onClick={() => handleCategoryClick(cat.name)}
                    className={`flex flex-col items-center p-3 rounded-lg border-2 transition-all hover:shadow-md flex-shrink-0 ${selectedCategory === cat.name ? 'border-brand-primary bg-brand-primary/5' : 'border-neutral-200 hover:border-neutral-300'}`}
                  >
                    <img src={cat.image_url || "https://images.unsplash.com/photo-1509391366360-2e959784a276?w=100&q=60"} alt={cat.name} className="w-20 h-20 rounded-lg object-cover mb-2" loading="lazy" />
                    <span className="text-sm font-medium text-center line-clamp-2 w-20">{cat.name}</span>
                  </button>
                ))}
              </div>
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={() => scrollCategories('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md flex-shrink-0"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div>
          {/* Controls */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6 items-start sm:items-center justify-between">
            <div className="flex items-center gap-3 flex-wrap w-full sm:w-auto">
              {selectedCategory && (
                <Badge variant="secondary" className="gap-1 cursor-pointer hover:bg-neutral-200" onClick={() => setSelectedCategory("")}>
                  {selectedCategory} <span className="ml-1 text-neutral-400">&times;</span>
                </Badge>
              )}
              <span className="text-sm text-neutral-500">{total} products</span>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto overflow-x-auto pb-2 sm:pb-0">
              <Select value={brandFilter || "all"} onValueChange={(v) => setBrandFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-40 h-9 text-sm flex-shrink-0" data-testid="brand-filter-select">
                  <SelectValue placeholder="Brand" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Brands</SelectItem>
                  {brands.map(([b, n]) => (
                    <SelectItem key={b} value={b}>{b} ({n})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={stockFilter} onValueChange={setStockFilter}>
                <SelectTrigger className="w-32 h-9 text-sm flex-shrink-0" data-testid="stock-filter-select">
                  <SelectValue placeholder="Stock" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stock</SelectItem>
                  <SelectItem value="true">In Stock</SelectItem>
                  <SelectItem value="false">Out of Stock</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="w-40 h-9 text-sm flex-shrink-0" data-testid="sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="featured">Featured</SelectItem>
                  <SelectItem value="name_asc">Name (A-Z)</SelectItem>
                  <SelectItem value="name_desc">Name (Z-A)</SelectItem>
                  <SelectItem value="price_asc">Price (Low-High)</SelectItem>
                  <SelectItem value="price_desc">Price (High-Low)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator className="mb-6" />

          {/* Product Grid */}
          {loading && products.length === 0 ? (
            <div data-testid="product-grid" className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 sm:gap-4">
              {[...Array(10)].map((_, i) => (
                <ProductCard key={`skeleton-${i}`} loading />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-neutral-500 text-lg">No products found</p>
              <p className="text-neutral-400 text-sm mt-1">Try adjusting your search or filter</p>
            </div>
          ) : (
            <>
              <div data-testid="product-grid" className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 sm:gap-4">
                {products.map((product, i) => (
                  <div key={product.id} className="animate-fade-in-up" style={{ animationDelay: `${Math.min(i * 40, 400)}ms` }}>
                    <ProductCard product={product} />
                  </div>
                ))}
              </div>
              {/* Infinite scroll sentinel */}
              <div id="infinite-scroll-sentinel" className="h-4" />
              {loadingMore && (
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 sm:gap-4">
                  {[...Array(4)].map((_, i) => (
                    <ProductCard key={`skeleton-more-${i}`} loading />
                  ))}
                </div>
              )}
              {!hasMore && products.length > 0 && (
                <div className="text-center py-8 text-neutral-400 text-sm">
                  Showing all {total} products
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-16 border-t border-neutral-200 pt-8 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <img src="/glannu-logo.png" alt="Glannu" className="h-10 mb-3" />
            <p className="text-sm text-neutral-500 leading-relaxed">Glannu, Pune. Trusted distributor of solar PV system components and BOS materials.</p>
          </div>
          <div>
            <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-neutral-500 mb-3">Contact</h3>
            <p className="text-sm text-neutral-600">Phone: +91 70838 98947</p>
            <p className="text-sm text-neutral-600 mt-1">Email: info.glannu@gmail.com</p>
            <p className="text-sm text-neutral-600 mt-1">WhatsApp: +91 86056 57016</p>
          </div>
          <div>
            <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-neutral-500 mb-3">Quick Links</h3>
            <Link to="/" className="block text-sm text-neutral-600 hover:text-brand-primary">All Products</Link>
            <Link to="/calculator" className="block text-sm text-neutral-600 mt-1 hover:text-brand-primary">Solar Calculator</Link>
            <Link to="/stores" className="block text-sm text-neutral-600 mt-1 hover:text-brand-primary">Store Locator</Link>
            <Link to="/about" className="block text-sm text-neutral-600 mt-1 hover:text-brand-primary">About Us</Link>
            <Link to="/terms" className="block text-sm text-neutral-600 mt-1 hover:text-brand-primary">Terms &amp; Conditions</Link>
          </div>
        </div>
        <Separator className="my-6" />
        <div className="flex justify-center gap-5 mb-4">
          <a href="https://facebook.com/GlannuIndustries" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-neutral-400 hover:text-brand-primary"><Facebook className="h-5 w-5" /></a>
          <a href="https://instagram.com/glannuindustries" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-neutral-400 hover:text-brand-primary"><Instagram className="h-5 w-5" /></a>
          <a href="https://linkedin.com/company/glannu" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="text-neutral-400 hover:text-brand-primary"><Linkedin className="h-5 w-5" /></a>
          <a href="https://youtube.com/@glannuindustries" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="text-neutral-400 hover:text-brand-primary"><Youtube className="h-5 w-5" /></a>
          <a href="https://twitter.com/glannuindustries" target="_blank" rel="noopener noreferrer" aria-label="Twitter / X" className="text-neutral-400 hover:text-brand-primary"><Twitter className="h-5 w-5" /></a>
          <a href="https://wa.me/918605657016" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="text-neutral-400 hover:text-green-600"><MessageCircle className="h-5 w-5" /></a>
        </div>
        <p className="text-xs text-neutral-400 text-center">&copy; {new Date().getFullYear()} Glannu. All rights reserved.</p>
      </footer>
    </main>
  );
}
